import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { PhaseShiftParams } from './phase-shift.model';

/**
ad * Creates phase shift visualization for periodic functions (sine, cosine, tangent).
 *
 * Features:
 * - Vertical dashed finite reference line from the curve point at x = c up to the arrow level
 * - Horizontal one-sided arrow from origin (x = 0) to the phase shift line
 *
 * Subparts:
 * - referenceLine: Vertical dashed finite line from curve point to arrow level at x = c
 * - arrow: Horizontal arrow from origin to phase shift
 *
 * For all periodic functions, phase shift = c parameter
 */
export class PhaseShiftCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'phaseShift';

  private readonly supportedTypes = ['sine', 'cosine', 'tangent-fn'];

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as PhaseShiftParams | undefined;

    if (!params?.of) {
      throw new Error('PhaseShift feature requires params.of to specify the parent function');
    }

    const parentElement = context.elements.get(params.of);
    if (!parentElement) {
      throw new Error(`Parent element ${params.of} not found`);
    }

    if (!this.supportedTypes.includes(parentElement.type)) {
      throw new Error(
        `PhaseShift feature only supports ${this.supportedTypes.join(', ')} functions, got ${parentElement.type}`,
      );
    }

    const objects: GeometryElement[] = [];

    // Resolve styles for each subpart
    const referenceLineStyle = resolveFeatureStyle('line', this.featureType, context, display, ['referenceLine']);
    const arrowStyle = resolveFeatureStyle('line', this.featureType, context, display, ['arrow']);

    const referenceLineVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['referenceLine']);
    const arrowVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['arrow']);

    // Helper to get function params reactively
    const getParams = () => parentElement.getParams?.() ?? {};

    // Get the phase shift value (c parameter)
    const getPhaseShift = () => getParams()['c'] ?? 0;

    // Get the y position for the arrow
    // For sine/cosine: at maximum level
    // For tangent: at a fixed height above d
    const getArrowY = () => {
      const p = getParams();
      if (parentElement.type === 'tangent-fn') {
        return (p['d'] ?? 0) + 3;
      }
      return (p['d'] ?? 0) + Math.abs(p['a'] ?? 1);
    };

    // Get the y position on the curve at x = c (the phase shift reference point)
    // For sine: at x = c, sin(0) = 0, so y = d
    // For cosine: at x = c, cos(0) = 1, so y = a + d
    // For tangent: at x = c, tan(0) = 0, so y = d
    const getCurveYAtPhaseShift = () => {
      const p = getParams();
      const a = p['a'] ?? 1;
      const d = p['d'] ?? 0;
      if (parentElement.type === 'cosine') {
        // cos(0) = 1, so y = a + d
        return a + d;
      }
      // sine and tangent: sin(0) = tan(0) = 0, so y = d
      return d;
    };

    // Reference line: vertical dashed finite line from curve point to arrow level at x = c
    const referenceLineResult = elementFactory.line({
      a: [getPhaseShift, getCurveYAtPhaseShift],
      b: [getPhaseShift, getArrowY],
      name: 'phaseShiftLine',
      style: {
        ...referenceLineStyle,
        visible: referenceLineVisible,
        straightFirst: false,
        straightLast: false,
      },
    });
    objects.push(referenceLineResult.line);
    referenceLineResult.a.setAttribute({ visible: false });
    referenceLineResult.b.setAttribute({ visible: false });
    if (referenceLineResult.lineLabel) {
      objects.push(referenceLineResult.lineLabel);
    }

    // Arrow: horizontal one-sided arrow from origin (x = 0) to phase shift
    const phaseArrowResult = elementFactory.line({
      a: [0, getArrowY],
      b: [getPhaseShift, getArrowY],
      name: 'c',
      style: {
        ...arrowStyle,
        visible: arrowVisible,
        arrows: { first: false, last: true },
      },
    });
    objects.push(phaseArrowResult.line);
    phaseArrowResult.a.setAttribute({ visible: false });
    phaseArrowResult.b.setAttribute({ visible: false });
    if (phaseArrowResult.lineLabel) {
      objects.push(phaseArrowResult.lineLabel);
    }

    return {
      id,
      type: 'phaseShift',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
